import { Box, Button, CircularProgress, Typography } from "@mui/material";

import PreviewSerialList from "./PreviewSerialList";
import { BandHead, FormBand, FormCard, Helper, FORM_TOKENS } from "./WorkOrderFormShared";

// The dry-run result: every serial checked against where it actually is, and
// against the route, with nothing saved.
//
// WHY IT IS ITS OWN COMPONENT. It used to be MovementBuilder's fourth band,
// which was fine while the form was one column -- but a panel that lives
// inside the left column cannot be rendered beside it. Lifting it out is what
// lets the page put it in a rail at wide widths and back in the stack at
// narrow ones, without MovementBuilder knowing either layout exists.
//
// THE READING ORDER STILL HOLDS. The old arrangement put this below the Route
// panel on purpose: the verdicts are not a property of the serials alone --
// "Forward needs a destination (distributor, subDistributor, dealer)" is a
// sentence about the route, returned per serial -- so a check shown above the
// route could only ever refuse fields the reader had not reached yet. Beside
// the form, that ordering argument stops applying, because the route is
// already on screen when the answer appears. Stacked, it still applies, and
// the page keeps this after both the route and the remarks.
const WorkOrderPreviewPanel = ({
    checkState = null,
    serialReport = null,
    // Rendered as the panel's last band: Submit, what will be recorded, Back.
    // THE ACTIONS BELONG WITH THE VERDICT. A Submit at the foot of the form
    // asks the user to commit at the one place on the page where the answer to
    // "can this be submitted" is not visible; beside the report that answers
    // it, the button and its justification are read together.
    actions = null,
    onRetryCheck = null,
    recheckComing = false,
    onRemoveSerials = null,
    disabled = false,
}) => {
    const check = checkState || {};
    const problems = serialReport?.problemSerials?.length || 0;

    // THE HEADING IS THE VERDICT. "Preview" is the right word for a panel with
    // nothing in it yet; once the check has answered, the one thing the reader
    // needs from the top of the card is whether anything is wrong, and how
    // much. Burying that under a generic title made them read the rows to find
    // out.
    const title = problems
        ? `${problems} need attention`
        : "Preview";

    // Whether the answer below describes the form as it stands. Three states,
    // and the quiet one matters: "up to date" is what tells a reader the
    // absence of red is a result rather than a panel that has not run yet.
    const freshness = check.status === "checking"
        ? null
        : check.isStale
            ? (recheckComing ? "rechecking" : "out of date")
            : (serialReport ? "up to date" : null);

    return (
        <FormCard>
            <FormBand last={!actions} testId="work-order-preview-band">
                <BandHead
                    title={title}
                    titleColor={problems ? FORM_TOKENS.danger : undefined}
                    // THE STATE, NOT A BUTTON. The check runs itself whenever
                    // the serials or the route change -- see the automatic
                    // check in WorkOrderForm -- so there is nothing here for
                    // the user to press. What is left to say is whether the
                    // answer below is current, being recomputed, or waiting on
                    // a field that is not filled in yet.
                    //
                    // A RETRY appears only after a transport failure, which is
                    // the one case the form cannot resolve on its own: it will
                    // not loop against a server that is down.
                    action={
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 1.25,
                                flexWrap: "wrap",
                            }}
                        >
                            {freshness && (
                                <Typography
                                    data-testid="work-order-check-freshness"
                                    sx={{
                                        fontSize: "12px",
                                        color: FORM_TOKENS.inkFaint,
                                    }}
                                >
                                    {freshness}
                                </Typography>
                            )}
                            {check.status === "checking" && (
                                <Box
                                    data-testid="work-order-check-running"
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        fontSize: "12px",
                                        color: FORM_TOKENS.inkSoft,
                                    }}
                                >
                                    <CircularProgress size={12} color="inherit" />
                                    Checking
                                </Box>
                            )}
                            {onRetryCheck && (
                                <Button
                                    variant="outlined"
                                    data-testid="work-order-check-serials"
                                    onClick={onRetryCheck}
                                    // Mid-flight it is not a retry, it is a
                                    // second request. checkFailed is not
                                    // cleared until the answer lands, so this
                                    // button stays mounted while the retry is
                                    // still out.
                                    disabled={disabled || check.status === "checking"}
                                    sx={CHECK_BUTTON_SX}
                                >
                                    Try again
                                </Button>
                            )}
                        </Box>
                    }
                />

                {/* Every field in the form stays filled in. Dimmed rather than
                    removed once the box or the route stops matching it: a
                    result that no longer describes the form reads as current,
                    and that is worse than none. */}
                {serialReport ? (
                    <PreviewSerialList
                        report={serialReport}
                        dimmed={Boolean(check.isStale)}
                        onRemoveSerials={onRemoveSerials}
                    />
                ) : (
                    <Helper text="Every serial is checked against where it actually is, and against the route, as soon as both are filled in. Nothing is saved." />
                )}
            </FormBand>

            {actions ? (
                <FormBand last testId="work-order-actions">
                    {actions}
                </FormBand>
            ) : null}
        </FormCard>
    );
};

// Only the colour differs from the shared action buttons, and deliberately: an
// outlined PURPLE button says "this is the form's own action" where the grey
// CancelFormActionButton says "this leaves". Emphasis is a choice; size is not.
const CHECK_BUTTON_SX = {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "10px",
    padding: "8px 24px",
    whiteSpace: "nowrap",
    borderColor: FORM_TOKENS.purple,
    color: FORM_TOKENS.purpleDark,
    "&:hover": {
        borderColor: FORM_TOKENS.purple,
        backgroundColor: FORM_TOKENS.purpleLight,
    },
};

export default WorkOrderPreviewPanel;
